import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  useAdminGetConsultantHourPackages,
  useAdminUpdateConsultantHourPackage,
  getAdminGetConsultantHourPackagesQueryKey,
} from '@/api/auth/admin/admin';
import type { ConsultantHourPackageResponse } from '@/api/auth/missionImpactAuthAPI.schemas';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Pencil } from 'lucide-react';
import { toast } from 'sonner';
import { logger } from '@/services/logger';
import { formatMinorUnits } from '@/lib/format';
import { formatHours } from '@/lib/hours';
import { AddConsultantHourPackageDialog } from './AddConsultantHourPackageDialog';
import { EditConsultantHourPackageDialog } from './EditConsultantHourPackageDialog';

interface ConsultantHourPackagesFormProps {
  productId: string;
}

const COLUMN_COUNT = 6;

/**
 * Manages the consultant hour packages a product offers. Consuming applications list the active
 * packages and start a Stripe checkout for one; the hours are credited when payment completes.
 */
export function ConsultantHourPackagesForm({ productId }: ConsultantHourPackagesFormProps) {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<ConsultantHourPackageResponse | null>(null);

  const { data, isLoading, error } = useAdminGetConsultantHourPackages(productId);
  const packages = data?.data?.packages ?? [];

  const { mutate: updatePackage, isPending: isToggling } = useAdminUpdateConsultantHourPackage({
    mutation: {
      onSuccess: (_, variables) => {
        toast.success(variables.data.active ? 'Package activated' : 'Package deactivated');
        queryClient.invalidateQueries({
          queryKey: getAdminGetConsultantHourPackagesQueryKey(productId),
        });
      },
      onError: (error: any) => {
        logger.error(error, { context: 'ToggleConsultantHourPackage', productId });
        toast.error('Failed to update package. ' + (error.response?.data?.detail || error.message));
      },
    },
  });

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-4 space-y-0">
        <div className="space-y-1.5">
          <CardTitle>Consultant Hour Packages</CardTitle>
          <CardDescription>
            Bundles of consultant hours this product sells for a one-time Stripe price
          </CardDescription>
        </div>
        <AddConsultantHourPackageDialog
          productId={productId}
          usedStripePriceIds={packages.map((p) => p.stripePriceId)}
        />
      </CardHeader>
      <CardContent>
        {error ? (
          <p className="text-destructive py-2">Failed to load consultant hour packages.</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead className="text-right">Hours</TableHead>
                <TableHead className="text-right">Price</TableHead>
                <TableHead>Stripe price</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                [...Array(2)].map((_, index) => (
                  <TableRow key={index}>
                    {[...Array(COLUMN_COUNT)].map((__, cell) => (
                      <TableCell key={cell}>
                        <Skeleton className="h-4 w-[80px]" />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : packages.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={COLUMN_COUNT}
                    className="text-center py-6 text-muted-foreground"
                  >
                    No consultant hour packages configured for this product.
                  </TableCell>
                </TableRow>
              ) : (
                packages.map((pkg) => (
                  <TableRow key={pkg.id}>
                    <TableCell className="font-medium">{pkg.displayName}</TableCell>
                    <TableCell className="text-right font-mono">{formatHours(pkg.hours)}</TableCell>
                    <TableCell className="text-right font-mono">
                      {formatMinorUnits(pkg.unitAmount, pkg.currency)}
                    </TableCell>
                    <TableCell className="font-mono text-xs">{pkg.stripePriceId}</TableCell>
                    <TableCell>
                      {!pkg.active ? (
                        <Badge variant="secondary">Inactive</Badge>
                      ) : !pkg.stripePriceActive ? (
                        <Badge variant="destructive">Price archived</Badge>
                      ) : (
                        <Badge>Active</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right space-x-2">
                      <Button
                        variant="ghost"
                        size="icon"
                        aria-label={`Edit ${pkg.displayName}`}
                        onClick={() => setEditing(pkg)}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={isToggling}
                        onClick={() =>
                          updatePackage({
                            productId,
                            packageId: pkg.id,
                            data: { active: !pkg.active },
                          })
                        }
                      >
                        {pkg.active ? 'Deactivate' : 'Activate'}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        )}
      </CardContent>
      <EditConsultantHourPackageDialog
        productId={productId}
        package={editing}
        onClose={() => setEditing(null)}
      />
    </Card>
  );
}
